import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { PageShell } from '@components/common/PageShell'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  CONTRIBUTION_KIND_LABELS,
  ContributionKind,
  ContributionStatus,
  type ContributionModerationDto,
} from '@api/types/contributions'
import { useContributionQueue, useDecideContribution } from '../hooks/useContributions'
import { ContributionComparison } from '../components/contributions/ContributionComparison'

/** The portal has no adventure page of its own; the adventure is shown on the player site. */
const PLAYER_SITE_URL = 'https://play.hadv.quest'

/**
 * The queue of changes people have suggested to adventures they do not own.
 *
 * A list of cards rather than a table. Every decision here needs the proposal and the thing it
 * would replace side by side — two descriptions, two weeks of opening hours, two photographs — and
 * a table row cannot hold that, so a table would mean opening each one to find out what it says.
 * At this volume the queue is read straight through.
 */
export function ContributionModerationPage() {
  const [status, setStatus] = useState<ContributionStatus>(ContributionStatus.Pending)
  const [kind, setKind] = useState<ContributionKind | 'all'>('all')
  const [deciding, setDeciding] = useState<{
    row: ContributionModerationDto
    action: 'approve' | 'reject'
  } | null>(null)
  const [reason, setReason] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const mobileDialogProps = useMobileDialogProps()

  // The queue is fetched whole, so paging is a slice. Cards are tall; ten of them is already a
  // long scroll on a phone.
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const params = useMemo(
    () => ({ status, ...(kind === 'all' ? {} : { kind }) }),
    [status, kind]
  )
  const { data, isLoading, isFetching, error, refetch } = useContributionQueue(params)
  const { approve, reject } = useDecideContribution()

  // Cleared when the dialog opens rather than when it closes, so a reason typed for one submission
  // can never be submitted against the next one.
  useEffect(() => {
    if (deciding) setReason('')
  }, [deciding])

  const rows = useMemo(() => data ?? [], [data])
  const busy = approve.isPending || reject.isPending

  // A narrower filter can leave the admin standing on a page that no longer exists.
  const maxPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1)
  const safePage = Math.min(page, maxPage)
  const pageRows = useMemo(
    () => rows.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [rows, safePage, pageSize]
  )

  // Top and bottom: on a phone a page of tall cards puts the bottom pager screens away.
  const pager = (position: 'top' | 'bottom') => (
    <TablePagination
      component="div"
      count={rows.length}
      page={safePage}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={pageSize}
      onRowsPerPageChange={(e) => {
        setPageSize(parseInt(e.target.value, 10))
        setPage(0)
      }}
      rowsPerPageOptions={[10, 25, 50]}
      labelRowsPerPage="Per page"
      sx={{ [position === 'top' ? 'mb' : 'mt']: 1, '& .MuiTablePagination-toolbar': { px: { xs: 1, sm: 2 }, flexWrap: 'wrap' } }}
    />
  )

  const confirm = async () => {
    if (!deciding) return
    try {
      if (deciding.action === 'approve') {
        await approve.mutateAsync({ id: deciding.row.id, note: reason.trim() || undefined })
        setNotice('Accepted, and applied to the adventure.')
      } else {
        await reject.mutateAsync({ id: deciding.row.id, reason: reason.trim() })
        setNotice('Turned down. The contributor has been told why.')
      }
      setDeciding(null)
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        'That did not work.'
      setNotice(message)
    }
  }

  return (
    <PageShell
      title="Suggestions"
      subtitle="Changes proposed by people who do not own the adventure. Accepting applies the change."
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void refetch()} disabled={isFetching}>
          Refresh
        </Button>
      }
    >
      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ sm: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={status}
            onChange={(_e, value) => {
              if (value === null) return
              setStatus(value)
              setPage(0)
            }}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value={ContributionStatus.Pending}>Waiting</ToggleButton>
            <ToggleButton value={ContributionStatus.Approved}>Accepted</ToggleButton>
            <ToggleButton value={ContributionStatus.Rejected}>Turned down</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            size="small"
            label="Kind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value === 'all' ? 'all' : (Number(e.target.value) as ContributionKind))
              setPage(0)
            }}
            sx={{ minWidth: { sm: 180 } }}
          >
            <MenuItem value="all">All kinds</MenuItem>
            {Object.entries(CONTRIBUTION_KIND_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      {/* A failed load is only an error: "Nothing waiting" under it would be a claim we cannot make. */}
      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()} disabled={isFetching}>
              Retry
            </Button>
          }
        >
          Could not load the queue.
        </Alert>
      ) : isLoading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {status === ContributionStatus.Pending
              ? 'Nothing waiting.'
              : 'Nothing here.'}
          </Typography>
        </Paper>
      ) : (
        <>
          {pager('top')}
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            {pageRows.map((row) => (
              <ContributionCard
                key={row.id}
                row={row}
                onDecide={(action) => setDeciding({ row, action })}
                disabled={busy}
              />
            ))}
          </Stack>
          {pager('bottom')}
        </>
      )}

      <Dialog
        open={!!deciding}
        onClose={() => !busy && setDeciding(null)}
        maxWidth="sm"
        fullWidth
        {...mobileDialogProps}
      >
        <DialogTitle>
          {deciding?.action === 'approve' ? 'Accept this suggestion?' : 'Turn this suggestion down?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {deciding?.action === 'approve'
              ? deciding.row.kind === ContributionKind.Other
                ? 'This records the note and tells the owner. Nothing on the adventure changes.'
                : 'This applies the change to the adventure straight away.'
              : 'The reason is shown to the person who sent it, so make it useful to them.'}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={deciding?.action === 'approve' ? 'Note (optional)' : 'Why not'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeciding(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={deciding?.action === 'approve' ? 'success' : 'error'}
            onClick={() => void confirm()}
            // A refusal without a reason is refused here as well as on the server: it is the one
            // thing the contributor actually receives.
            disabled={busy || (deciding?.action === 'reject' && !reason.trim())}
          >
            {deciding?.action === 'approve' ? 'Accept and apply' : 'Turn down'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        message={notice ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageShell>
  )
}

function ContributionCard({
  row,
  onDecide,
  disabled,
}: {
  row: ContributionModerationDto
  onDecide: (action: 'approve' | 'reject') => void
  disabled: boolean
}) {
  const decided = row.status !== ContributionStatus.Pending
  const seen = row.contributorApprovedCount + row.contributorRejectedCount

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={1} mb={1}>
        <Chip size="small" label={CONTRIBUTION_KIND_LABELS[row.kind] ?? 'Unknown'} />
        <Link
          href={`${PLAYER_SITE_URL}/adventures/${row.adventureId}`}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {row.adventureTitle}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {row.contributorDisplayName} · {new Date(row.submittedAt).toLocaleString()}
        </Typography>
      </Stack>

      {/* The whole of the trust signal there is, and it is shown rather than acted on — nothing
          auto-approves yet. Thirty accepted and none refused is a different proposition from an
          account that opened this morning, and the queue should say which it is looking at. */}
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} mb={1.5}>
        {seen === 0 ? (
          <Chip size="small" variant="outlined" label="First suggestion from this account" />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            color={row.contributorRejectedCount === 0 ? 'success' : 'default'}
            label={`${row.contributorApprovedCount} accepted · ${row.contributorRejectedCount} turned down before`}
            // A chip never wraps its label; on a phone this one is wider than the screen.
            sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
          />
        )}
      </Stack>

      <ContributionComparison row={row} />

      {row.note && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            <strong>Why:</strong> {row.note}
          </Typography>
        </>
      )}

      {decided ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, overflowWrap: 'anywhere' }}>
          {row.status === ContributionStatus.Approved ? 'Accepted' : 'Turned down'}
          {row.reviewedAt ? ` on ${new Date(row.reviewedAt).toLocaleDateString()}` : ''}
          {row.decisionReason ? ` — ${row.decisionReason}` : ''}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2} sx={{ '& .MuiButton-root': { minHeight: 40 } }}>
          <Button color="error" disabled={disabled} onClick={() => onDecide('reject')}>
            Turn down
          </Button>
          <Button variant="contained" color="success" disabled={disabled} onClick={() => onDecide('approve')}>
            Accept
          </Button>
        </Stack>
      )}
    </Paper>
  )
}

export default ContributionModerationPage
